import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Trophy, Gift, ShoppingBag, Sparkles } from "lucide-react";

// #48.5: De tre scoreboards (Omsætning / Gavekort-Klippekort / Produktsalg) — top 3
// byer som podie (guld/sølv/bronze). Bragt tilbage efter at de fejlagtigt blev
// fjernet i #45. Periode: indeværende måned (data fra month_department_ranking).
const PODIUM_RING = ["#FFD700", "#60A5FA", "#F97316"];
const PODIUM_BG = ["rgba(255,215,0,0.15)", "rgba(96,165,250,0.12)", "rgba(249,115,22,0.12)"];
const DISPLAY_NAMES = { Koebenhavn: "København" };
const displayName = (n) => DISPLAY_NAMES[n] || n;

const formatDKK = (amount) =>
  new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

function PodiumBox({ title, icon: Icon, data, valueKey, formatValue }) {
  const top3 = [...(data || [])].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0)).slice(0, 3);
  while (top3.length < 3) top3.push(null);
  const ordered = [top3[1], top3[0], top3[2]]; // 2., 1., 3.
  const heights = ["h-16", "h-24", "h-10"];

  return (
    <Card className="bg-card border-border shadow-none flex-1">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#FF2A6D]" />
          <CardTitle className="text-sm font-bold tracking-tight">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-center gap-2 pt-2 pb-1">
          {ordered.map((item, i) => {
            const rank = i === 1 ? 1 : i === 0 ? 2 : 3;
            const isFirst = rank === 1;
            if (!item) return (
              <div key={`empty-${rank}`} className="flex flex-col items-center gap-2 flex-1">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ border: `2px solid ${PODIUM_RING[rank - 1]}30` }}
                />
                <div className={`w-full ${heights[i]} rounded-t-md flex items-center justify-center`}
                  style={{ background: `${PODIUM_RING[rank - 1]}15` }}>
                  <span className="text-lg font-bold" style={{ color: `${PODIUM_RING[rank - 1]}50` }}>{rank}</span>
                </div>
              </div>
            );
            return (
              <div key={item.department} className="flex flex-col items-center gap-1 flex-1">
                <div className="relative">
                  <div
                    className={`${isFirst ? "w-14 h-14" : "w-11 h-11"} rounded-full flex items-center justify-center font-bold`}
                    style={{
                      background: `linear-gradient(135deg, ${PODIUM_BG[rank - 1]}, rgba(255,255,255,0.05))`,
                      border: `${isFirst ? "3px" : "2px"} solid ${PODIUM_RING[rank - 1]}`,
                      color: PODIUM_RING[rank - 1],
                      fontSize: isFirst ? "13px" : "11px",
                      boxShadow: isFirst ? `0 0 16px ${PODIUM_RING[rank - 1]}30` : "none",
                    }}
                  >
                    {displayName(item.department).slice(0, 3).toUpperCase()}
                  </div>
                  {isFirst && (
                    <Sparkles className="absolute -top-2 -right-1 w-3.5 h-3.5 text-amber-400 animate-pulse-glow" />
                  )}
                  <div
                    className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center text-[12px] font-bold text-black"
                    style={{ backgroundColor: PODIUM_RING[rank - 1] }}
                  >
                    {rank}
                  </div>
                </div>
                <div
                  className={`w-full ${heights[i]} rounded-t-md flex flex-col items-center justify-center gap-0.5 mt-2`}
                  style={{ background: `${PODIUM_RING[rank - 1]}18` }}
                >
                  <span className="text-[13px] font-bold text-foreground text-center leading-tight px-1">
                    {displayName(item.department)}
                  </span>
                  <span className="text-[13px] font-kpi" style={{ color: PODIUM_RING[rank - 1] }}>
                    {formatValue(item[valueKey] || 0)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function ScoreboardPodier({ ranking }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <PodiumBox title="Omsætning" icon={Trophy} data={ranking} valueKey="total" formatValue={formatDKK} />
      <PodiumBox title="Gavekort / Klippekort" icon={Gift} data={ranking} valueKey="gavekort" formatValue={formatDKK} />
      <PodiumBox title="Produktsalg" icon={ShoppingBag} data={ranking} valueKey="products" formatValue={formatDKK} />
    </div>
  );
}

export default ScoreboardPodier;
